import RouteMap from '../components/RouteMap';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { track } from '../utils/track';
import './QuoteRequest.css';

const SERVICES = {
  business: {
    title: 'Business Delivery Quote',
    label: 'Business Services',
    description: 'Tell us about your business delivery requirements.',
  },
  house: {
    title: 'House & Flat Moving Quote',
    label: 'House & Flat Moves',
    description: 'Tell us about your move and where you need to go.',
  },
  large: {
    title: 'Large Item Delivery Quote',
    label: 'Large Item Deliveries',
    description: 'Tell us what you need transported.',
  },
};

const INITIAL = {
  name: '',
  email: '',
  phone: '',
  company: '',
  pickup: '',
  dropoff: '',
  moveDate: '',
  noDate: false,
  businessService: '',
  details: '',
};

export default function QuoteRequest() {
  const [params, setParams] = useSearchParams();
  const requestedService = params.get('service');
  const serviceKey = Object.prototype.hasOwnProperty.call(
    SERVICES, requestedService
  ) ? requestedService : '';

  const service = SERVICES[serviceKey];
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle');

  const update = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!service) return;

    setStatus('sending');

    const details = [
      `Service: ${service.label}`,
      form.company && `Company: ${form.company}`,
      form.businessService && `Business service: ${form.businessService}`,
      `Pickup: ${form.pickup}`,
      `Drop-off: ${form.dropoff}`,
      `Date: ${form.noDate ? 'Not decided yet' : form.moveDate || 'Not specified'}`,
      `Goods / moving details: ${form.details}`
    ].filter(Boolean).join('\n');

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          enquiryType: service.label,
          name: form.name,
          email: form.email,
          phone: form.phone,
          pickup: form.pickup,
          dropoff: form.dropoff,
          moveDate: form.noDate ? 'Not decided yet' : form.moveDate,
          message: details,
          time: new Date().toLocaleString('en-GB'),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      track('generate_lead', { form_name: service.label });
      setStatus('sent');
      setForm(INITIAL);
    } catch (error) {
      console.error('Quote request failed:', error);
      setStatus('error');
    }
  };

  return (
    <main className="usl-request-page">
      <div className="usl-request-shell">

        <div className="usl-request-heading">
          <h1>{service ? service.title : 'Get a Free Quote'}</h1>
          <p>
            {service
              ? service.description
              : 'Choose your service and tell us what you need.'}
          </p>
        </div>

        <div className="usl-request-grid">

          <form className="usl-request-form" onSubmit={submit}>
            <label>
              Select your service*
              <select
                required
                value={serviceKey}
                onChange={e => {
                  const next = e.target.value;
                  setParams(next ? { service: next } : {});
                  setForm(INITIAL);
                  setStatus('idle');
                }}
              >
                <option value="">Choose a service</option>
                <option value="business">Business Services</option>
                <option value="house">House & Flat Moves</option>
                <option value="large">Large Item Deliveries</option>
              </select>
            </label>

            {service && (
              <>
                {serviceKey === 'business' && (
                  <>
                    <label>
                      Company name*
                      <input
                        name="company"
                        value={form.company}
                        onChange={update}
                        required
                      />
                    </label>

                    <label>
                      Type of business delivery*
                      <select
                        name="businessService"
                        value={form.businessService}
                        onChange={update}
                        required
                      >
                        <option value="">Select service</option>
                        <option>Same Day Delivery</option>
                        <option>Overnight Delivery</option>
                        <option>Multi-Drop Delivery</option>
                        <option>Bulk Collections</option>
                        <option>Other Business Transport</option>
                      </select>
                    </label>
                  </>
                )}

                <div className="usl-request-locations">
                  <label>
                    Pickup postcode or address*
                    <input
                      name="pickup"
                      value={form.pickup}
                      onChange={update}
                      placeholder="Collection location"
                      required
                    />
                  </label>

                  <label>
                    Delivery postcode or address*
                    <input
                      name="dropoff"
                      value={form.dropoff}
                      onChange={update}
                      placeholder="Delivery location"
                      required
                    />
                  </label>
                </div>

                <div className="usl-request-date">
                  <label>
                    {serviceKey === 'business' ? 'Collection date' : 'Move date'}
                    <input
                      type="date"
                      name="moveDate"
                      value={form.moveDate}
                      onChange={update}
                      disabled={form.noDate}
                    />
                  </label>

                  <label className="usl-request-check">
                    <input
                      type="checkbox"
                      checked={form.noDate}
                      onChange={e =>
                        setForm(prev => ({
                          ...prev,
                          noDate: e.target.checked,
                          moveDate: e.target.checked ? '' : prev.moveDate
                        }))
                      }
                    />
                    I don't have a date yet
                  </label>
                </div>

                <label>
                  {serviceKey === 'business'
                    ? 'Describe your delivery requirements*'
                    : 'What needs moving?*'}
                  <textarea
                    name="details"
                    rows={4}
                    required
                    value={form.details}
                    onChange={update}
                    placeholder={
                      serviceKey === 'business'
                        ? 'Type of goods, quantity, size and any special requirements'
                        : 'e.g. 2-seater sofa, double bed and 6 boxes'
                    }
                  />
                </label>

                <h2>Your Contact Details</h2>

                <div className="usl-request-locations">
                  <label>
                    Full name*
                    <input
                      name="name"
                      value={form.name}
                      onChange={update}
                      autoComplete="name"
                      required
                    />
                  </label>

                  <label>
                    Phone number*
                    <input
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={update}
                      autoComplete="tel"
                      required
                    />
                  </label>
                </div>

                <label>
                  Email address*
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={update}
                    autoComplete="email"
                    required
                  />
                </label>

                <button
                  className="usl-request-submit"
                  type="submit"
                  disabled={status === 'sending'}
                >
                  {status === 'sending'
                    ? 'Sending…'
                    : 'Send Quote Request'}
                </button>

                {status === 'sent' && (
                  <p role="status" className="usl-request-success">
                    Thank you! We've received your request and will be in touch.
                  </p>
                )}

                {status === 'error' && (
                  <p role="alert" className="usl-request-error">
                    Something went wrong. Please try again.
                  </p>
                )}
              </>
            )}
          </form>

          <aside className="usl-request-map-panel">
            <h2>Collection &amp; Delivery Map</h2>
            <RouteMap
              pickup={form.pickup}
              dropoff={form.dropoff}
            />
          </aside>

        </div>
      </div>
    </main>
  );
}
